import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardSidebar } from '../components/DashboardSidebar';
import { SupportTicketPanel } from '../components/SupportTicketPanel';
import { Server, Users, DollarSign, Activity, Shield, HardDrive } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const chartData = [{
  name: 'Aug',
  spend: 380
}, {
  name: 'Sep',
  spend: 420
}, {
  name: 'Oct',
  spend: 410
}, {
  name: 'Nov',
  spend: 450
}, {
  name: 'Dec',
  spend: 459
}, {
  name: 'Jan',
  spend: 459
}];
export function BusinessDashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const stats = [{
    label: 'Active Services',
    value: '12',
    icon: Server,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  }, {
    label: 'Team Members',
    value: '8',
    icon: Users,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10'
  }, {
    label: 'Monthly Spend',
    value: '$459.88',
    icon: DollarSign,
    color: 'text-fox-500',
    bg: 'bg-fox-500/10'
  }, {
    label: 'Uptime (30d)',
    value: '99.99%',
    icon: Activity,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10'
  }];
  const infrastructure = [{
    name: 'Prod DB Server',
    type: 'Dedicated Server',
    ip: '192.168.1.100',
    status: 'Healthy',
    usage: '45%'
  }, {
    name: 'App Server 01',
    type: 'VPS Server',
    ip: '192.168.1.101',
    status: 'Healthy',
    usage: '62%'
  }, {
    name: 'App Server 02',
    type: 'VPS Server',
    ip: '192.168.1.102',
    status: 'Healthy',
    usage: '58%'
  }, {
    name: 'Load Balancer',
    type: 'Network',
    ip: '192.168.1.50',
    status: 'Healthy',
    usage: '20%'
  }];
  const Overview = () => <>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4" data-id="element-1481">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1482">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1483">
            Acme Corp Infrastructure
          </h1>
          <p className="text-slate-400" data-id="element-1484">
            Enterprise Dashboard & Resource Management
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1485">
          <button className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-lg shadow-fox-500/20" data-id="element-1486">
            Deploy New Service
          </button>
        </motion.div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-1487">
        {stats.map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1488">
            <div className="flex items-center justify-between mb-4" data-id="element-1489">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-1490">
                <stat.icon size={24} data-id="element-1491" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1" data-id="element-1492">{stat.value}</h3>
            <p className="text-slate-400 text-sm font-medium" data-id="element-1493">{stat.label}</p>
          </motion.div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1494">
        {/* Infrastructure List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 space-y-6" data-id="element-1495">
          <div className="flex items-center justify-between" data-id="element-1496">
            <h2 className="text-xl font-heading font-bold text-white" data-id="element-1497">
              Core Infrastructure
            </h2>
            <div className="flex gap-2" data-id="element-1498">
              <button className="p-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors" data-id="element-1499">
                <Shield size={18} data-id="element-1500" />
              </button>
              <button className="p-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors" data-id="element-1501">
                <HardDrive size={18} data-id="element-1502" />
              </button>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-1503">
            <table className="w-full text-left border-collapse" data-id="element-1504">
              <thead data-id="element-1505">
                <tr className="border-b border-slate-800 text-slate-400 text-sm" data-id="element-1506">
                  <th className="p-4 font-medium" data-id="element-1507">Resource Name</th>
                  <th className="p-4 font-medium" data-id="element-1508">Type</th>
                  <th className="p-4 font-medium" data-id="element-1509">IP Address</th>
                  <th className="p-4 font-medium" data-id="element-1510">Status</th>
                  <th className="p-4 font-medium" data-id="element-1511">CPU Usage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800" data-id="element-1512">
                {infrastructure.map((item, i) => <tr key={i} className="hover:bg-slate-900/50 transition-colors" data-id="element-1513">
                    <td className="p-4 font-medium text-white" data-id="element-1514">{item.name}</td>
                    <td className="p-4 text-slate-400 text-sm" data-id="element-1515">{item.type}</td>
                    <td className="p-4 text-slate-400 text-sm font-mono" data-id="element-1516">
                      {item.ip}
                    </td>
                    <td className="p-4" data-id="element-1517">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium border border-emerald-500/20" data-id="element-1518">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-slow" data-id="element-1519"></div>
                        {item.status}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1520">
                      <div className="flex items-center gap-3" data-id="element-1521">
                        <div className="w-full bg-slate-800 rounded-full h-1.5 max-w-[60px]" data-id="element-1522">
                          <div className="bg-fox-500 h-1.5 rounded-full" style={{
                        width: item.usage
                      }} data-id="element-1523"></div>
                        </div>
                        <span className="text-xs text-slate-400" data-id="element-1524">
                          {item.usage}
                        </span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Billing Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="space-y-6" data-id="element-1525">
          <div className="flex items-center justify-between" data-id="element-1526">
            <h2 className="text-xl font-heading font-bold text-white" data-id="element-1527">
              Spend History
            </h2>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 h-[320px] flex flex-col" data-id="element-1528">
            <div className="mb-4" data-id="element-1529">
              <span className="text-3xl font-bold text-white" data-id="element-1530">$459.88</span>
              <span className="text-slate-400 text-sm ml-2" data-id="element-1531">Current Month</span>
            </div>

            <div className="flex-1 w-full min-h-0" data-id="element-1532">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1533">
                <BarChart data={chartData} margin={{
                top: 10,
                right: 0,
                left: -20,
                bottom: 0
              }} data-id="element-1534">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-1535" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} data-id="element-1536" />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `$${value}`} data-id="element-1537" />
                  <Tooltip cursor={{
                  fill: '#1e293b'
                }} contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#1e293b',
                  borderRadius: '8px'
                }} itemStyle={{
                  color: '#ea580c'
                }} data-id="element-1538" />
                  <Bar dataKey="spend" fill="#ea580c" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-1539" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>
    </>;
  return <div className="min-h-screen bg-slate-900 flex" data-id="element-1540">
      <DashboardSidebar type="business" isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} data-id="element-1541" />

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0" data-id="element-1542">
        <div className="p-6 md:p-10 max-w-7xl mx-auto" data-id="element-1543">
          <Routes data-id="element-1544">
            <Route path="/" element={<Overview data-id="element-1546" />} data-id="element-1545" />
            <Route path="/support" element={<SupportTicketPanel userType="business" userName="Acme Corp" userEmail="billing@acme.com" data-id="element-1548" />} data-id="element-1547" />
            <Route path="*" element={<Navigate to="/dashboard/business" replace data-id="element-1550" />} data-id="element-1549" />
          </Routes>
        </div>
      </main>
    </div>;
}